import React, { useState, useMemo, useRef, useEffect, useCallback } from 'react'
import dayjs from 'dayjs'
import CustomDataTable from 'general/components/CustomDataTable'
import DraggableModal from 'general/components/Modal/DragableModal/DraggableModal'
import customDataTableStyle from 'assets/styles/customDataTableStyle'
import TableAction from 'general/components/GroupButton/TableAction'
import { IconAction } from 'general/components/GroupButton/styles'
import Utils from 'general/utils/Utils'
import InvoiceHelper from 'general/helpers/InvoiceHelper'
import ToastHelper from 'general/helpers/ToastHelper'
import { INVOICE_STATUS } from 'general/constants/AppConstants'
import invoiceApi from 'api/invoiceApi'
import PreferenceKeys from 'general/constants/PreferenceKeys'
import { useAppDispatch, useAppSelector } from 'hooks/useRedux'
import { setAppSpinning } from 'app/appSlice'
import ModalConfirm from 'general/components/Modal/ModalConfirm'
import { Checkbox } from 'antd'
import WarningNotAcceptedHelper from 'general/helpers/WarningNotAcceptedHelper'

const reasonMap = {
  INVOICE_INVALID: 'Hóa đơn không hợp lệ',
}

export default function WarningNotAcceptedModal({
  open,
  onCancel,
  classify,
  onViewInvoice,
  onSuccess,
  onCountChange,
  filterDismissed = false,
}) {
  const modalRef = useRef(null)
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 20,
  })
  const [tableData, setTableData] = useState({
    rows: [],
    count: 0,
    loading: false,
  })
  const [resendingId, setResendingId] = useState(null)
  const dispatch = useAppDispatch()
  const { activeSigningMethod } = useAppSelector(
    (state) => state?.system?.company || {},
  )
  const [hsmSignModalInfo, setHSMSignModalInfo] = useState({
    show: false,
    invoiceIds: [],
  })
  const [dontShowAgain, setDontShowAgain] = useState(false)
  const displayedRowIdsRef = useRef(new Set())

  const title = useMemo(() => {
    if (classify === 'PXK') {
      return 'Danh sách phiếu xuất kho gửi không được chấp nhận'
    }
    if (classify === 'MTT') {
      return 'Danh sách hóa đơn máy tính tiền gửi không được chấp nhận'
    }
    return 'Danh sách hóa đơn gửi không được chấp nhận'
  }, [classify])

  const description = useMemo(() => {
    if (classify === 'PXK') {
      return 'Hệ thống phát hiện có phiếu xuất kho đã gửi nhưng không được Cơ quan thuế chấp nhận. Vui lòng kiểm tra lý do và xử lý lại.'
    }
    if (classify === 'MTT') {
      return 'Hệ thống phát hiện có hóa đơn máy tính tiền đã gửi nhưng không được Cơ quan thuế chấp nhận. Vui lòng kiểm tra lý do và xử lý lại.'
    }
    return 'Hệ thống phát hiện có các hóa đơn đã gửi nhưng không được Cơ quan thuế chấp nhận. Vui lòng kiểm tra lý do và xử lý lại.'
  }, [classify])

  const tableKey = useMemo(() => {
    if (classify) {
      return `warning-not-accepted-${classify.toLowerCase()}-list`
    }
    return 'warning-not-accepted-invoice-list'
  }, [classify])

  const fetchData = useCallback(async () => {
    setTableData((prev) => ({ ...prev, loading: true }))
    try {
      const params = {
        ...(classify ? { classify } : {}),
        all: true,
        page: pagination.page || 1,
        limit: pagination.limit || 20,
      }
      const res = await invoiceApi.getWarningNotAccepted(params)
      const rows = Array.isArray(res)
        ? res
        : res?.rows || res?.data || res?.invoices || res?.list || []
      const responseInvoiceIds = res?.invoiceIds || res?.data?.invoiceIds
      const invoiceIds = Array.isArray(responseInvoiceIds)
        ? responseInvoiceIds
        : rows.map((row) => row?.invoiceId || row?.id).filter(Boolean)
      const dismissedIds = new Set(
        filterDismissed ? WarningNotAcceptedHelper.getDismissedIds() : [],
      )
      const visibleRows = filterDismissed
        ? rows.filter((row) => {
            const invoiceId = row?.invoiceId || row?.id
            return !invoiceId || !dismissedIds.has(String(invoiceId))
          })
        : rows
      const responseCount = res?.count ?? rows.length ?? 0
      const count = filterDismissed ? visibleRows.length : responseCount
      onCountChange?.(responseCount)
      setTableData({
        rows: visibleRows,
        count,
        invoiceIds,
        loading: false,
      })
    } catch (err) {
      console.error('Error fetching warning not accepted invoices:', err)
      setTableData((prev) => ({ ...prev, loading: false }))
    }
  }, [classify, filterDismissed, onCountChange, pagination])

  useEffect(() => {
    if (open) {
      setDontShowAgain(false)
      displayedRowIdsRef.current.clear()
      fetchData()
    }
  }, [open, fetchData])

  useEffect(() => {
    if (open && tableData.rows.length > 0) {
      tableData.rows.forEach((r) => {
        const id = r?.invoiceId || r?.id
        if (id) displayedRowIdsRef.current.add(String(id))
      })
      if (dontShowAgain) {
        WarningNotAcceptedHelper.addDismissed(
          Array.from(displayedRowIdsRef.current),
        )
      }
    }
  }, [open, tableData.rows, dontShowAgain])

  const handleToggleDontShowAgain = useCallback(
    (checked) => {
      setDontShowAgain(checked)
      const currentIds = tableData.invoiceIds || []

      currentIds.forEach((id) => displayedRowIdsRef.current.add(String(id)))
      const allDisplayedIds = Array.from(displayedRowIdsRef.current)

      if (checked) {
        WarningNotAcceptedHelper.addDismissed(allDisplayedIds)
      } else {
        WarningNotAcceptedHelper.removeDismissed(allDisplayedIds)
      }
    },
    [tableData.invoiceIds],
  )

  const handleClose = useCallback(() => {
    if (dontShowAgain && displayedRowIdsRef.current.size > 0) {
      WarningNotAcceptedHelper.addDismissed(
        Array.from(displayedRowIdsRef.current),
      )
    }
    onCancel?.()
  }, [dontShowAgain, onCancel])

  useEffect(() => {
    if (!open) return

    const handleReload = () => {
      fetchData()
      onSuccess?.()
    }

    window.addEventListener('REFETCH_WARNING_NOT_ACCEPTED', handleReload)

    const mttBtn = document.getElementById('refetchSummariInvoice')
    const invBtn = document.getElementById('refetchInvoice')

    if (mttBtn) mttBtn.addEventListener('click', handleReload)
    if (invBtn) invBtn.addEventListener('click', handleReload)

    return () => {
      window.removeEventListener('REFETCH_WARNING_NOT_ACCEPTED', handleReload)
      if (mttBtn) mttBtn.removeEventListener('click', handleReload)
      if (invBtn) invBtn.removeEventListener('click', handleReload)
    }
  }, [open, fetchData, onSuccess])

  const reIssueInvoice = useCallback(
    async (row) => {
      const invoiceId = row?.invoiceId || row?.id
      if (!invoiceId || resendingId) return

      setResendingId(invoiceId)
      try {
        let res = await invoiceApi.reIssue({ invoiceIds: [invoiceId] })
        if (res?.data) res = res.data

        const success = res?.reIssueSuccessInvoices?.some(
          ({ invoiceId: successInvoiceId }) => successInvoiceId == invoiceId,
        )
        if (success) {
          ToastHelper.showSuccess('Phát hành lại hóa đơn thành công')
          onSuccess?.()
          document.getElementById('refetchInvoice')?.click()
        } else {
          ToastHelper.showError(
            res?.reIssueErrorInvoices?.[0]?.reason ||
              'Phát hành lại không thành công',
          )
        }
        fetchData()
      } catch (err) {
        console.error('Error re-issuing invoice:', err)
        ToastHelper.showError('Phát hành lại không thành công')
      } finally {
        setResendingId(null)
      }
    },
    [fetchData, onSuccess, resendingId],
  )

  const handleHsmSignSummaryInvoice = useCallback(async () => {
    const invoiceIds = hsmSignModalInfo.invoiceIds
    if (!invoiceIds || !invoiceIds.length) return

    dispatch(setAppSpinning(true))
    try {
      await invoiceApi.hsmSignSummaryInvoice({ invoiceIds })
      dispatch(setAppSpinning(false))
      ToastHelper.showSuccess('Gửi dữ liệu MTT đến CQT thành công')
      onSuccess?.()
      document.getElementById('refetchSummariInvoice')?.click()
    } catch (e) {
      dispatch(setAppSpinning(false))
      console.error('Error signing HSM summary invoice:', e)
    } finally {
      setHSMSignModalInfo({ show: false, invoiceIds: [] })
      fetchData()
    }
  }, [dispatch, fetchData, hsmSignModalInfo.invoiceIds, onSuccess])

  const reIssueInvoiceMTT = useCallback(
    async (row) => {
      const invoiceId = row?.invoiceId || row?.id
      if (!invoiceId || resendingId) return

      try {
        if (activeSigningMethod === 'HSM') {
          setHSMSignModalInfo({
            show: true,
            invoiceIds: [invoiceId],
          })
        } else {
          setResendingId(invoiceId)
          const accessToken = localStorage.getItem(PreferenceKeys.accessToken)
          const urlSchema = `${process.env.REACT_APP_BASE_URL_SCHEMA}${accessToken},summaryInvoiceSignedBySeller,${invoiceId}`

          Utils.openToolSignSummaryInvoice(urlSchema, accessToken)
        }
      } catch (err) {
        console.error('Error re-issuing MTT invoice:', err)
        ToastHelper.showError('Phát hành lại không thành công')
      } finally {
        if (activeSigningMethod !== 'HSM') {
          setResendingId(null)
        }
      }
    },
    [activeSigningMethod, resendingId],
  )

  const columns = useMemo(
    () => [
      {
        id: 'serial',
        name: 'Ký hiệu',
        minWidth: '110px',
        center: true,
        cell: (row) => (
          <p
            style={{ textAlign: 'center' }}
            data-tag="allowRowEvents"
            className="text-dark-75 font-weight-bold m-0 text-maxline-3 w-100"
          >
            {row?.serial || row?.symbol || '-'}
          </p>
        ),
      },
      {
        id: 'no',
        name: classify === 'PXK' ? 'Số phiếu' : 'Số',
        minWidth: '110px',
        center: true,
        cell: (row) => (
          <p
            style={{ textAlign: 'center' }}
            data-tag="allowRowEvents"
            className="text-dark-75 font-weight-bold m-0 text-maxline-3 w-100"
          >
            {row?.no
              ? InvoiceHelper.padNumber(row.no, 8)
              : row?.invoiceNo
                ? InvoiceHelper.padNumber(row.invoiceNo, 8)
                : '-'}
          </p>
        ),
      },
      {
        id: 'date',
        name: classify === 'PXK' ? 'Ngày lập' : 'Ngày hoá đơn',
        minWidth: '130px',
        center: true,
        cell: (row) => {
          const dateVal = row?.date || row?.invoiceDate || row?.createdAt
          return (
            <p
              style={{ textAlign: 'center' }}
              data-tag="allowRowEvents"
              className="text-dark-75 font-weight-bold m-0 text-maxline-3 w-100"
            >
              {dayjs(dateVal).isValid()
                ? dayjs(dateVal).format('L')
                : dateVal || '-'}
            </p>
          )
        },
      },
      {
        id: 'customerTaxCode',
        name: 'Mã số thuế',
        center: true,
        minWidth: '140px',
        cell: (row) => (
          <p
            style={{ textAlign: 'center' }}
            data-tag="allowRowEvents"
            className="text-dark-75 font-weight-bold m-0 text-maxline-3 w-100"
          >
            {row?.customerTaxCode || row?.taxCode || '-'}
          </p>
        ),
      },
      {
        id: 'customerCompanyName',
        name: 'Khách hàng',
        center: true,
        minWidth: '200px',
        cell: (row) => (
          <p
            style={{ textAlign: 'left' }}
            data-tag="allowRowEvents"
            className="text-dark-75 w-100 font-weight-bold m-0 text-maxline-3"
          >
            {row?.customerCompanyName || row?.customerName || '-'}
          </p>
        ),
      },
      {
        id: 'totalAfterTax',
        name: 'Tổng tiền thanh toán',
        center: true,
        minWidth: '140px',
        cell: (row) => (
          <p
            style={{ textAlign: 'right' }}
            data-tag="allowRowEvents"
            className="text-danger font-weight-bold m-0 text-maxline-3 w-100"
          >
            {Utils.formatNumber(
              row?.totalAfterTax ?? row?.totalMoney ?? row?.total ?? 0,
            )}
          </p>
        ),
      },
      {
        id: 'reason',
        name: 'Lý do không chấp nhận',
        center: true,
        minWidth: '260px',
        cell: (row) => (
          <p
            style={{
              textAlign: 'left',
              whiteSpace: 'pre-line',
            }}
            data-tag="allowRowEvents"
            className="text-dark-75 font-weight-bold m-0 text-maxline-3 w-100"
          >
            {row?.errorMessage ?? reasonMap[row?.sendTaxStatus] ?? 'Gửi lỗi'}
          </p>
        ),
      },
      {
        name: 'Hành động',
        center: true,
        disabled: true,
        compact: true,
        minWidth: '130px',
        style: {
          justifyContent: 'center',
        },
        cell: (row) => (
          <div className="d-flex justify-content-center">
            <TableAction
              titleActionText="Xem"
              icon={<IconAction className="fa-light fa-eye" />}
              onClick={() => {
                if (onViewInvoice) {
                  onViewInvoice(row)
                }
              }}
            />
            {[row?.sendTaxStatus, row?.issueStatus].some((status) =>
              [
                INVOICE_STATUS.SEND_ERROR,
                INVOICE_STATUS.AUTHORITY_DENY,
              ].includes(status),
            ) && (
              <TableAction
                titleActionText="Gửi lại"
                icon={<IconAction className="fa-light fa-rotate-right" />}
                isLoading={resendingId === (row?.invoiceId || row?.id)}
                onClick={() =>
                  classify === 'MTT'
                    ? reIssueInvoiceMTT(row)
                    : reIssueInvoice(row)
                }
              />
            )}
          </div>
        ),
      },
    ],
    [classify, onViewInvoice, reIssueInvoice, reIssueInvoiceMTT, resendingId],
  )

  return (
    <>
      <DraggableModal
        ref={modalRef}
        title={title}
        description={description}
        status="warning"
        isOpen={open}
        onOpenChange={(isOpen) => {
          if (!isOpen) handleClose()
        }}
        hasApplyButton={false}
        hasCancelButton={true}
        cancelText="Đóng"
        width="85%"
        extraFooter={
          <Checkbox
            checked={dontShowAgain}
            onChange={(e) => handleToggleDontShowAgain(e.target.checked)}
          >
            <span style={{ fontSize: '14px', color: '#2b2b2b' }}>
              Không hiển thị lại
            </span>
          </Checkbox>
        }
      >
        <CustomDataTable
          tableKey={tableKey}
          columns={columns}
          dataSource={tableData.rows}
          isLoading={tableData.loading}
          scrollY="400px"
          customTableStyle={customDataTableStyle}
          pagination={{
            currentPage: pagination.page || 1,
            rowsPerPage: pagination.limit || 20,
            total: tableData.count,
            onChangePage: (newPage) => {
              setPagination((prev) => ({
                ...prev,
                page: parseInt(newPage, 10),
              }))
            },
            onChangeRowsPerPage: (newLimit) => {
              setPagination((prev) => ({
                ...prev,
                page: 1,
                limit: parseInt(newLimit, 10),
              }))
            },
          }}
          persistTableHead
          noShowEmpty={false}
        />
      </DraggableModal>
      <div style={{ display: 'none' }}>
        <button
          id="refetchWarningNotAccepted"
          onClick={() => {
            fetchData()
          }}
        >
          refetch
        </button>
      </div>
      <ModalConfirm
        show={hsmSignModalInfo.show}
        setShow={(value) =>
          setHSMSignModalInfo((old) => ({
            ...old,
            show: value,
          }))
        }
        header="Ký số HSM"
        content="Chương trình sẽ thực hiện ký số HSM. Bạn chắc chắn vẫn ký?"
        handleConfirm={handleHsmSignSummaryInvoice}
      />
    </>
  )
}
